/**
 * Bundle Spec Service — loads V3 block specs and matches them against block names.
 *
 * Block specs define required flows, events, and UI elements per block type.
 * This is the Studio-side bridge to the Bundle-First methodology.
 */

// Static imports of all V3 block specs (small JSON, safe to bundle)
import productGridSpec from '@/data/bundle-specs/product-grid.spec.json'
import productDetailSpec from '@/data/bundle-specs/product-detail.spec.json'
import cartViewSpec from '@/data/bundle-specs/cart-view.spec.json'
import checkoutFormSpec from '@/data/bundle-specs/checkout-form.spec.json'
import storeNavSpec from '@/data/bundle-specs/store-nav.spec.json'
import dataTableSpec from '@/data/bundle-specs/data-table.spec.json'
import chartSpec from '@/data/bundle-specs/chart.spec.json'
import formSpec from '@/data/bundle-specs/form.spec.json'
import carouselSpec from '@/data/bundle-specs/carousel.spec.json'

// ---- Types ----

export interface BlockSpecV3 {
  $schema: string
  blockType: string
  description: string
  matchNames: string[]
  ui?: {
    iterateOver?: { target: string; itemVar: string; enforce?: string }
    requiredElements?: Array<{
      role: string
      context?: string
      matchBy: { text?: string[]; id?: string[]; className?: string[] }
    }>
    requiredDisplayBindings?: Array<{
      role: string
      matchBy: { id?: string[] }
      requiredText: string
    }>
  }
  flow?: {
    required: Record<string, {
      trigger: string
      steps: Array<{ action: string; params?: Record<string, unknown> }>
    }>
  }
  events?: {
    required: Record<string, {
      flow: string
      params?: Record<string, string>
    }>
  }
  state?: Record<string, unknown>
  data?: Record<string, unknown>
}

// ---- Registry ----

const ALL_SPECS: BlockSpecV3[] = [
  productGridSpec as unknown as BlockSpecV3,
  productDetailSpec as unknown as BlockSpecV3,
  cartViewSpec as unknown as BlockSpecV3,
  checkoutFormSpec as unknown as BlockSpecV3,
  storeNavSpec as unknown as BlockSpecV3,
  dataTableSpec as unknown as BlockSpecV3,
  chartSpec as unknown as BlockSpecV3,
  formSpec as unknown as BlockSpecV3,
  carouselSpec as unknown as BlockSpecV3,
]

// ---- API ----

/** Match a block name against all specs. Returns the best match or null. */
export function detectBlockSpec(blockName: string): BlockSpecV3 | null {
  if (!blockName) return null
  const lower = blockName.toLowerCase()

  // Exact match on matchNames
  for (const spec of ALL_SPECS) {
    if (spec.matchNames.some((n) => lower === n || lower.includes(n) || n.includes(lower))) {
      return spec
    }
  }

  // Keyword match: split block name and check overlap
  const words = lower.split(/[-_\s]+/)
  let bestSpec: BlockSpecV3 | null = null
  let bestScore = 0
  for (const spec of ALL_SPECS) {
    const specWords = spec.blockType.split(/[-_\s]+/)
    const overlap = words.filter((w) => specWords.some((sw) => sw.includes(w) || w.includes(sw))).length
    const score = overlap / Math.max(words.length, specWords.length)
    if (score > bestScore && score >= 0.5) {
      bestScore = score
      bestSpec = spec
    }
  }

  return bestSpec
}

/** Get required flow names from a spec. */
export function getRequiredFlows(spec: BlockSpecV3): Array<{ name: string; trigger: string; stepCount: number }> {
  if (!spec.flow?.required) return []
  return Object.entries(spec.flow.required).map(([name, def]) => ({
    name,
    trigger: def.trigger,
    stepCount: def.steps.length,
  }))
}

/** Get required event bindings from a spec. */
export function getRequiredEvents(spec: BlockSpecV3): Array<{ pattern: string; flow: string; hasParams: boolean }> {
  if (!spec.events?.required) return []
  return Object.entries(spec.events.required).map(([pattern, def]) => ({
    pattern,
    flow: def.flow,
    hasParams: !!def.params && Object.keys(def.params).length > 0,
  }))
}

/** Get required UI elements from a spec. */
export function getRequiredElements(spec: BlockSpecV3): Array<{ role: string; context?: string }> {
  return (spec.ui?.requiredElements ?? []).map((el) => ({
    role: el.role,
    context: el.context,
  }))
}

/** Get all loaded specs. */
export function getAllSpecs(): BlockSpecV3[] {
  return ALL_SPECS
}
